export interface ProductImageDimensions {
  width: number;
  height: number;
}

export type DesktopPdpHeroLayoutMode = "single-image" | "editorial" | "centered-wide";

const WIDE_FEATURED_RATIO_THRESHOLD = 1.15;
const PORTRAIT_SECONDARY_RATIO_THRESHOLD = 0.85;
const RATIO_CONTRAST_THRESHOLD = 1.4;

function getAspectRatio(dims: ProductImageDimensions | null): number | null {
  if (!dims) return null;
  if (dims.width <= 0 || dims.height <= 0) return null;

  const ratio = dims.width / dims.height;
  return Number.isFinite(ratio) ? ratio : null;
}

// Desktop PDP hero rules:
// - single-image: no right-column gallery, so keep the standard single-image layout.
// - centered-wide: the left hero is clearly wide and the first right image is clearly portrait,
//   so keep the sticky frame but center the hero inside it instead of pinning it to the top.
// - editorial: everything else, including portrait-led galleries, balanced pairs, inverse
//   tall-left / wide-right cases, and any case where dimensions are still unknown.
// - note: we do not specially handle the inverse tall-left / wide-right mismatch yet; that is
//   better treated as a follow-up layout pass than folded into this rule implicitly.
export function getDesktopPdpHeroLayoutMode({
  hasSecondaryImages,
  faceDims,
  firstSecondaryDims,
}: {
  hasSecondaryImages: boolean;
  faceDims: ProductImageDimensions | null;
  firstSecondaryDims: ProductImageDimensions | null;
}): DesktopPdpHeroLayoutMode {
  if (!hasSecondaryImages) {
    return "single-image";
  }

  const faceRatio = getAspectRatio(faceDims);
  const secondaryRatio = getAspectRatio(firstSecondaryDims);

  if (faceRatio === null || secondaryRatio === null) {
    return "editorial";
  }

  const faceIsWide = faceRatio >= WIDE_FEATURED_RATIO_THRESHOLD;
  const secondaryIsPortrait = secondaryRatio <= PORTRAIT_SECONDARY_RATIO_THRESHOLD;
  const ratioContrastIsLarge = faceRatio / secondaryRatio >= RATIO_CONTRAST_THRESHOLD;

  if (faceIsWide && secondaryIsPortrait && ratioContrastIsLarge) {
    return "centered-wide";
  }

  return "editorial";
}
